import { type ConsentInfoResponse } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { useBranding } from '@/branding/BrandingProvider';
import BrandingHeader from '@/components/BrandingHeader';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type ThirdPartyBranding = ConsentInfoResponse['application']['branding'];

type Props = {
  readonly children: ReactNode;
  readonly title: TFuncKey;
  readonly titleInterpolation?: Record<string, unknown>;
  readonly thirdPartyBranding?: ThirdPartyBranding;
  /**
   * Opt-in only. When `true`, replaces the stock i18n-driven `title` with the
   * brand's static `headline` (see `useBranding()`). Defaults to `false` so
   * `LandingPageLayout` — shared by SignIn, Register, Device, and Consent —
   * never silently overrides a security-relevant, interpolated title (e.g.
   * Consent's "which app is requesting access" disclosure) unless a page
   * explicitly asks for the branded headline. Only the SignIn page sets this.
   */
  readonly isBrandHeadlineOverrideEnabled?: boolean;
};

const LandingPageLayout = ({
  children,
  title,
  titleInterpolation,
  thirdPartyBranding,
  isBrandHeadlineOverrideEnabled = false,
}: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);
  const brand = useBranding();

  if (!experienceSettings) {
    return null;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title, titleKeyInterpolation: titleInterpolation }}>
      <BrandingHeader
        className={classNames(styles.header, layoutClassNames.brandingHeader)}
        headline={title}
        headlineInterpolation={titleInterpolation}
        headlineOverride={isBrandHeadlineOverrideEnabled ? brand.headline : undefined}
        logo={brand.logoUrl ?? getBrandingLogoUrl({ theme, branding, isDarkModeEnabled })}
        thirdPartyLogo={
          thirdPartyBranding &&
          getBrandingLogoUrl({ theme, branding: thirdPartyBranding, isDarkModeEnabled })
        }
      />
      {children}
    </FirstScreenLayout>
  );
};

export default LandingPageLayout;
